import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import JsonLd from '@/components/feature/JsonLd';
import ArticleCard from '@/components/feature/ArticleCard';
import { useSeo } from '@/hooks/useSeo';
import ArticleBody from '@/pages/article/components/ArticleBody';
import ArticleAside from '@/pages/article/components/ArticleAside';
import { articles } from '@/mocks/articles';
import { brand } from '@/mocks/site';
import { formatDate, formatViews } from '@/utils/format';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((item) => item.slug === slug);

  const headings = article
    ? article.body
        .map((block, index) => ({ ...block, index }))
        .filter((block) => block.type === 'h2')
        .map((block) => ({ index: block.index, text: block.text }))
    : [];

  const related = article
    ? [
        ...articles.filter((item) => item.categoryId === article.categoryId && item.id !== article.id),
        ...articles.filter((item) => item.categoryId !== article.categoryId && item.id !== article.id),
      ].slice(0, 3)
    : [];

  const relatedIds = new Set(related.map((item) => item.id));
  const moreArticles = article
    ? [...articles]
        .filter((item) => item.id !== article.id && !relatedIds.has(item.id))
        .sort((a, b) => b.views - a.views)
        .slice(0, 3)
    : [];

  useSeo({
    title: article ? `${article.title} | 揭秘局` : '线报详情 | 揭秘局',
    description: article
      ? `${article.excerpt}｜分类：${article.category}，附图解与信息图，持续跟进后续反映。`
      : '揭秘局独家线报与图解内幕详情页。',
    keywords: article ? [article.category, ...article.tags, '独家揭秘', '图解'].join(',') : '独家揭秘,线报,图解',
    path: `/article/${slug ?? ''}`,
    ogType: 'article',
    image: article?.cover,
  });

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 w-full px-4 md:px-6 py-20 text-center">
          <span className="inline-flex w-16 h-16 rounded-full bg-background-100 text-foreground-500 items-center justify-center">
            <i className="ri-article-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这篇线报不存在或已归档</h1>
          <p className="mt-3 text-sm text-foreground-600">链接可能已经调整，去线报库看看其他选题吧。</p>
          <Link
            to="/feed"
            className="mt-6 inline-flex items-center gap-1.5 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            返回线报库
            <i className="ri-arrow-right-line" />
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: [article.cover, ...article.gallery.map((g) => g.src)],
    datePublished: article.date,
    articleSection: article.category,
    keywords: article.tags.join(','),
    inLanguage: 'zh-CN',
    author: { '@type': 'Person', name: article.author },
    publisher: {
      '@type': 'Organization',
      name: brand.name,
      url: window.location.origin,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${window.location.origin}/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: window.location.origin },
      { '@type': 'ListItem', position: 2, name: '线报库', item: `${window.location.origin}/feed` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `${window.location.origin}/article/${article.slug}` },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 w-full">
        <article className="px-4 md:px-6 pt-8 md:pt-10">
          <nav aria-label="面包屑" className="text-xs text-foreground-500">
            <ol className="flex items-center gap-2 flex-wrap">
              <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line" /></li>
              <li><Link to="/feed" className="hover:text-primary-600 transition-colors">线报库</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line" /></li>
              <li><Link to={`/feed?cat=${article.categoryId}`} className="hover:text-primary-600 transition-colors">{article.category}</Link></li>
            </ol>
          </nav>

          <header className="mt-6 max-w-4xl">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-800 text-xs font-medium">
              {article.category}
            </span>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-snug">
              {article.title}
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>
            <div className="mt-5 flex items-center gap-4 flex-wrap text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-3-line" />
                {article.author} · {article.authorRole}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {formatDate(article.date)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line" />
                阅读约 {article.readTime} 分钟
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {formatViews(article.views)} 阅读
              </span>
            </div>
          </header>

          <figure className="mt-8 rounded-lg overflow-hidden border border-background-200/80">
            <div className="w-full h-56 md:h-[420px]">
              <img src={article.cover} alt={article.title} className="w-full h-full object-cover object-top" />
            </div>
            <figcaption className="px-4 py-3 bg-background-100 text-xs text-foreground-500">
              题图：{article.title}
            </figcaption>
          </figure>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <div className="lg:col-span-8">
              <ArticleBody blocks={article.body} />

              <section className="mt-12 rounded-lg border border-secondary-200 bg-secondary-50 p-6 md:p-7">
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-md bg-secondary-500 text-background-50 flex items-center justify-center">
                    <i className="ri-bar-chart-2-line" />
                  </span>
                  <h2 className="font-heading text-lg font-bold text-foreground-950">信息图 · 关键要点</h2>
                </div>
                <ul className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {article.keyPoints.map((point) => (
                    <li key={point} className="flex gap-2.5 rounded-md bg-background-50 p-4 text-sm text-foreground-700 leading-relaxed">
                      <i className="ri-checkbox-circle-fill text-secondary-600 mt-0.5 shrink-0" />
                      {point}
                    </li>
                  ))}
                </ul>
              </section>

              <section className="mt-12">
                <h2 className="font-heading text-lg font-bold text-foreground-950">图解内幕</h2>
                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {article.gallery.map((item) => (
                    <figure key={item.src} className="rounded-lg overflow-hidden border border-background-200/80">
                      <div className="w-full h-52">
                        <img src={item.src} alt={item.alt} className="w-full h-full object-cover object-top" />
                      </div>
                      <figcaption className="px-4 py-3 bg-background-50 text-xs text-foreground-600 leading-relaxed">
                        {item.caption}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </section>

              <section className="mt-12 rounded-lg bg-foreground-950 text-background-100 p-6 md:p-8">
                <span className="inline-flex items-center gap-2 text-xs font-label tracking-[0.28em] uppercase text-accent-400">
                  <span className="w-6 h-px bg-accent-500" />
                  后续反映
                </span>
                <h2 className="mt-3 font-heading text-lg md:text-xl font-bold text-background-50">事件跟进时间线</h2>
                <ol className="mt-6 space-y-5">
                  {article.timeline.map((item) => (
                    <li key={item.time} className="relative pl-7">
                      <span className="absolute left-0 top-1 w-3 h-3 rounded-full bg-accent-500" />
                      <span className="absolute left-[5px] top-4 bottom-[-20px] w-px bg-secondary-700" />
                      <div className="text-xs font-medium text-accent-400">{item.time}</div>
                      <p className="mt-1 text-sm text-background-200 leading-relaxed">{item.text}</p>
                    </li>
                  ))}
                </ol>
              </section>

              <div className="mt-10 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-500">相关标签：</span>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/feed?q=${encodeURIComponent(tag)}`}
                    className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>

            <ArticleAside headings={headings} related={related} />
          </div>
        </article>

        <section className="mt-16 px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="flex items-end justify-between gap-4">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">继续看下去</h2>
            <Link
              to="/feed"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              更多线报
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {moreArticles.map((item) => (
              <ArticleCard key={item.id} article={item} />
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
      <JsonLd id={`ld-article-${article.id}`} data={articleSchema} />
      <JsonLd id={`ld-article-breadcrumb-${article.id}`} data={breadcrumbSchema} />
    </div>
  );
}